import Vue from 'vue'
import Toast from 'vue-toastification'
import { Settings } from '@/Settings'

// Toast Notification Component Styles
import '@core/scss/vue/libs/toastification.scss'

/**
 * NOTE: If you are using other transition them make sure to import it in `src/@core/scss/vue/libs/notification.scss` from it's source
 */
Vue.use(Toast, {
  hideProgressBar: true,
  closeOnClick: false,
  closeButton: false,
  icon: false,
  timeout: 3000,
  transition: 'Vue-Toastification__fade',
})

/**
 * Iframe toast redirection (KP-2646)
 *
 * When B2B runs inside a PLG iframe, intercept all toast calls and forward
 * them to the PLG parent via postMessage so PLG renders them with sonner.
 *
 * Maps vue-toastification variants to PLG toast types:
 *   success → success | warning → warning | danger → error | * → info
 */
const isInsideIframe = window !== window.parent

if (isInsideIframe) {
  const VARIANT_MAP = {
    success: 'success',
    warning: 'warning',
    danger: 'error',
    error: 'error',
    primary: 'info',
    info: 'info',
  }

  const ACK_TIMEOUT_MS = 1500
  const pendingToasts = new Map()
  let toastIdCounter = 0

  const getPlgOrigin = () => {
    try {
      return Settings.getConfig('VUE_APP_PLG_URL')
    } catch (_) {
      return null
    }
  }

  window.addEventListener('message', event => {
    const plgOrigin = getPlgOrigin()
    if (!plgOrigin || event.origin !== plgOrigin) return
    const { data } = event
    if (!data || data.type !== 'MESSAGE_ACK' || data.originalType !== 'SHOW_TOAST') return

    const entry = pendingToasts.get(data.originalTimestamp)
    if (entry) {
      clearTimeout(entry.timer)
      pendingToasts.delete(data.originalTimestamp)
    }
  })

  const extractPropsDetails = props => ({
    variant: props.variant || null,
    title: props.title || '',
    text: props.text || '',
  })

  const extractToastDetails = (content, variant) => {
    if (content && content.props) {
      const details = extractPropsDetails(content.props)
      return { ...details, variant: details.variant || variant || 'info' }
    }
    return {
      variant: variant || 'info',
      title: typeof content === 'string' ? content : '',
      text: '',
    }
  }

  const postToastMessage = ({
 content, options, variant, fallback,
}) => {
    const details = extractToastDetails(content, variant)
    const toastId = `${Date.now()}-${++toastIdCounter}`
    const plgOrigin = getPlgOrigin()

    if (!plgOrigin) {
      fallback(content, options)
      return
    }

    try {
      window.parent.postMessage(
        {
          type: 'SHOW_TOAST',
          toastType: VARIANT_MAP[details.variant] || 'info',
          message: details.title,
          description: details.text || undefined,
          timestamp: toastId,
        },
        plgOrigin,
      )

      const timer = setTimeout(() => {
        pendingToasts.delete(toastId)
        fallback(content, options)
      }, ACK_TIMEOUT_MS)

      pendingToasts.set(toastId, { timer, content, options })
    } catch (_) {
      fallback(content, options)
    }
  }

  const originalToast = Vue.prototype.$toast

  Vue.prototype.$toast = function patchedToast(content, options) {
    postToastMessage({
 content, options, variant: null, fallback: originalToast.bind(this),
})
  }

  const DISPLAY_METHODS = new Set(Object.keys(VARIANT_MAP))

  Object.keys(originalToast).forEach(key => {
    if (DISPLAY_METHODS.has(key)) {
      Vue.prototype.$toast[key] = function patchedShorthand(content, options) {
        postToastMessage({
 content, options, variant: key, fallback: originalToast[key].bind(this),
})
      }
    } else {
      Vue.prototype.$toast[key] = originalToast[key]
    }
  })
}
